import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, questionsAPI } from '../lib/api';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, 
  Users, 
  GitBranch, 
  Search,
  MoreVertical,
  UserX,
  UserCheck,
  Trash2,
  Crown,
  BarChart3,
  Bot,
  Play,
  Pause,
  RefreshCw,
  Zap,
  Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Bot state
  const [botStatus, setBotStatus] = useState(null);
  const [botLoading, setBotLoading] = useState(false);
  const [botConfig, setBotConfig] = useState({
    users_per_hour: 60,
    posts_per_hour: 30,
    comments_per_hour: 120,
    max_total_users: 500,
    max_total_posts: 200,
  });
  
  // Confirm Dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    action: null,
  });

  // Check admin access - wait for auth to finish loading
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, questionsRes, botRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers({ limit: 50 }),
        adminAPI.getQuestions({ limit: 50 }),
        adminAPI.getBotStatus().catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setQuestions(questionsRes.data.questions);
      if (botRes.data) {
        setBotStatus(botRes.data);
        if (botRes.data.config) {
          setBotConfig(botRes.data.config);
        }
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Bot handlers
  const fetchBotStatus = async () => {
    try {
      const response = await adminAPI.getBotStatus();
      setBotStatus(response.data);
      if (response.data.config) {
        setBotConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const handleStartBot = async () => {
    setBotLoading(true);
    try {
      await adminAPI.startBot();
      toast.success('Bot started');
      await fetchBotStatus();
    } catch (error) {
      toast.error('Failed to start bot');
    } finally {
      setBotLoading(false);
    }
  };

  const handleStopBot = async () => {
    setBotLoading(true);
    try {
      await adminAPI.stopBot();
      toast.success('Bot stopped');
      await fetchBotStatus();
    } catch (error) {
      toast.error('Failed to stop bot');
    } finally {
      setBotLoading(false);
    }
  };

  const handleRunOnce = async () => {
    setBotLoading(true);
    try {
      const response = await adminAPI.runBotOnce();
      toast.success(`Bot cycle: ${response.data.result.users_created} users, ${response.data.result.mindmaps_created} mindmaps, ${response.data.result.nodes_added} nodes`);
      await fetchBotStatus();
      await fetchData();
    } catch (error) {
      toast.error('Failed to run bot cycle');
    } finally {
      setBotLoading(false);
    }
  };

  const handleSeedContent = async () => {
    setBotLoading(true);
    try {
      const response = await adminAPI.seedContent(20, 10);
      toast.success(`Seeded: ${response.data.results.users_created} users, ${response.data.results.mindmaps_created} mindmaps`);
      await fetchBotStatus();
      await fetchData();
    } catch (error) {
      toast.error('Failed to seed content');
    } finally {
      setBotLoading(false);
    }
  };

  const handleDeleteBotContent = async () => {
    setConfirmDialog({
      open: true,
      title: 'Delete All Bot Content',
      description: 'This will permanently delete all bot-generated users, mindmaps, nodes, and comments. This action cannot be undone.',
      action: async () => {
        setBotLoading(true);
        try {
          const response = await adminAPI.deleteBotContent();
          toast.success(`Deleted: ${response.data.deleted.users} users, ${response.data.deleted.questions} mindmaps`);
          await fetchBotStatus();
          await fetchData();
        } catch (error) {
          toast.error('Failed to delete bot content');
        } finally {
          setBotLoading(false);
        }
      }
    });
  };

  const handleUpdateBotConfig = async () => {
    setBotLoading(true);
    try {
      await adminAPI.updateBotConfig(botConfig);
      toast.success('Bot config updated');
      await fetchBotStatus();
    } catch (error) {
      toast.error('Failed to update config');
    } finally {
      setBotLoading(false);
    }
  };

  const handleSearchUsers = async () => {
    try {
      const response = await adminAPI.getUsers({ search: searchQuery, limit: 50 });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleBlockUser = async (userId, currentlyBlocked) => {
    setConfirmDialog({
      open: true,
      title: currentlyBlocked ? 'Unblock User' : 'Block User',
      description: currentlyBlocked 
        ? 'Are you sure you want to unblock this user? They will be able to access the platform again.'
        : 'Are you sure you want to block this user? They will not be able to access the platform.',
      action: async () => {
        try {
          await adminAPI.updateUser(userId, { is_blocked: !currentlyBlocked });
          setUsers(prev => prev.map(u => 
            u.user_id === userId ? { ...u, is_blocked: !currentlyBlocked } : u
          ));
          toast.success(currentlyBlocked ? 'User unblocked' : 'User blocked');
        } catch (error) {
          toast.error('Action failed');
        }
      },
    });
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const isAdmin = currentRole === 'admin';
    setConfirmDialog({
      open: true,
      title: isAdmin ? 'Remove Admin' : 'Make Admin',
      description: isAdmin 
        ? 'Are you sure you want to remove admin privileges from this user?'
        : 'Are you sure you want to give this user admin privileges?',
      action: async () => {
        try {
          await adminAPI.updateUser(userId, { role: isAdmin ? 'user' : 'admin' });
          setUsers(prev => prev.map(u => 
            u.user_id === userId ? { ...u, role: isAdmin ? 'user' : 'admin' } : u
          ));
          toast.success(isAdmin ? 'Admin removed' : 'Admin added');
        } catch (error) {
          toast.error('Action failed');
        }
      },
    });
  };

  const handleDeleteUser = async (userId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      description: 'Are you sure you want to permanently delete this user? This action cannot be undone.',
      action: async () => {
        try {
          await adminAPI.deleteUser(userId);
          setUsers(prev => prev.filter(u => u.user_id !== userId));
          toast.success('User deleted');
        } catch (error) {
          toast.error('Delete failed');
        }
      },
    });
  };

  const handleDeleteQuestion = async (questionId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Question',
      description: 'Are you sure you want to delete this question and all its nodes? This action cannot be undone.',
      action: async () => {
        try {
          await questionsAPI.delete(questionId);
          setQuestions(prev => prev.filter(q => q.question_id !== questionId));
          toast.success('Question deleted');
        } catch (error) {
          toast.error('Delete failed');
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500">Manage users and content</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                  <p className="text-xs text-slate-500">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <UserX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.blocked_users}</p>
                  <p className="text-xs text-slate-500">Blocked</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <GitBranch className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_questions}</p>
                  <p className="text-xs text-slate-500">Questions</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_nodes}</p>
                  <p className="text-xs text-slate-500">Nodes</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_comments}</p>
                  <p className="text-xs text-slate-500">Comments</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="bot" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Content Bot
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <div className="flex gap-2">
                  <div className="search-bar flex-1">
                    <Search className="search-icon w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Button onClick={handleSearchUsers} className="btn-primary" data-testid="admin-search-btn">
                    Search
                  </Button>
                </div>
              </div>
              
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium overflow-hidden">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                              u.name?.charAt(0).toUpperCase()
                            )}
                          </div>
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="text-slate-500">{u.email}</td>
                      <td>
                        <span className={`badge ${u.role === 'admin' ? 'badge-orange' : 'badge-slate'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <span className={`flex items-center gap-1.5 ${u.is_blocked ? 'text-red-500' : 'text-green-500'}`}>
                          <span className={`status-dot ${u.is_blocked ? 'blocked' : 'active'}`}></span>
                          {u.is_blocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="text-slate-500">
                        {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : 'N/A'}
                      </td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleBlockUser(u.user_id, u.is_blocked)}
                              data-testid={`block-user-${u.user_id}`}
                            >
                              {u.is_blocked ? (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Unblock
                                </>
                              ) : (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  Block
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleToggleAdmin(u.user_id, u.role)}
                              data-testid={`toggle-admin-${u.user_id}`}
                            >
                              <Crown className="w-4 h-4 mr-2" />
                              {u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteUser(u.user_id)}
                              className="text-red-600"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Author</th>
                    <th>Nodes</th>
                    <th>Contributors</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr key={q.question_id} data-testid={`question-row-${q.question_id}`}>
                      <td>
                        <span 
                          className="font-medium text-slate-900 hover:text-orange-500 cursor-pointer"
                          onClick={() => navigate(`/q/${q.question_id}`)}
                        >
                          {q.title}
                        </span>
                      </td>
                      <td className="text-slate-500">{q.author_name}</td>
                      <td>{q.node_count}</td>
                      <td>{q.contributor_count}</td>
                      <td className="text-slate-500">
                        {q.created_at ? format(new Date(q.created_at), 'MMM d, yyyy') : 'N/A'}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteQuestion(q.question_id)}
                          className="text-red-500 hover:text-red-600"
                          data-testid={`delete-question-${q.question_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Bot Tab */}
          <TabsContent value="bot">
            <div className="space-y-6">
              {/* Bot Status Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${botStatus?.is_running ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Bot className={`w-6 h-6 ${botStatus?.is_running ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Content Bot</h3>
                      <p className={`text-sm ${botStatus?.is_running ? 'text-green-600' : 'text-slate-500'}`}>
                        {botStatus?.is_running ? 'Running' : 'Stopped'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {botStatus?.is_running ? (
                      <Button 
                        onClick={handleStopBot} 
                        disabled={botLoading}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Stop Bot
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleStartBot} 
                        disabled={botLoading}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Bot
                      </Button>
                    )}
                    <Button 
                      onClick={handleRunOnce} 
                      disabled={botLoading}
                      variant="outline"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Run Once
                    </Button>
                  </div>
                </div>

                {/* Bot Stats */}
                {botStatus && (
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{botStatus.bot_users || 0}</p>
                      <p className="text-xs text-slate-500">Bot Users</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{botStatus.bot_questions || 0}</p>
                      <p className="text-xs text-slate-500">Bot Mindmaps</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{botStatus.bot_nodes || 0}</p>
                      <p className="text-xs text-slate-500">Bot Nodes</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{botStatus.bot_comments || 0}</p>
                      <p className="text-xs text-slate-500">Bot Comments</p>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="flex gap-3">
                  <Button 
                    onClick={handleSeedContent} 
                    disabled={botLoading}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Seed Content (20 users, 10 mindmaps)
                  </Button>
                  <Button 
                    onClick={handleDeleteBotContent} 
                    disabled={botLoading}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All Bot Content
                  </Button>
                </div>
              </div>

              {/* Bot Configuration */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Settings className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-900">Bot Configuration</h3>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Users per hour
                    </label>
                    <Input
                      type="number"
                      value={botConfig.users_per_hour}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, users_per_hour: parseInt(e.target.value) || 0 }))}
                      className="w-full"
                    />
                    <p className="text-xs text-slate-500 mt-1">Rate: ~1 user every {Math.round(60 / (botConfig.users_per_hour || 1))} minutes</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Posts per hour
                    </label>
                    <Input
                      type="number"
                      value={botConfig.posts_per_hour}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, posts_per_hour: parseInt(e.target.value) || 0 }))}
                      className="w-full"
                    />
                    <p className="text-xs text-slate-500 mt-1">Rate: ~1 post every {Math.round(60 / (botConfig.posts_per_hour || 1))} minutes</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Comments per hour
                    </label>
                    <Input
                      type="number"
                      value={botConfig.comments_per_hour}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, comments_per_hour: parseInt(e.target.value) || 0 }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Max total bot users
                    </label>
                    <Input
                      type="number"
                      value={botConfig.max_total_users}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, max_total_users: parseInt(e.target.value) || 0 }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Max total bot posts
                    </label>
                    <Input
                      type="number"
                      value={botConfig.max_total_posts}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, max_total_posts: parseInt(e.target.value) || 0 }))}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <Button 
                    onClick={handleUpdateBotConfig} 
                    disabled={botLoading}
                    className="btn-primary"
                  >
                    Save Configuration
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-2">Content Categories</h4>
                  <p className="text-sm text-slate-600">
                    Bot users are assigned to one of these categories and create content only in their category:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {['Startup', 'SaaS', 'Investment', 'Scaling', 'Business', 'AI & Tech'].map(cat => (
                      <span key={cat} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>{confirmDialog.description}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                confirmDialog.action?.();
                setConfirmDialog(prev => ({ ...prev, open: false }));
              }}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-action-btn"
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
